import { User } from '../model/user';

/**
 * Safely reads the cached user from storage.
 *
 * Returns the parsed {@link User} when present, or an empty `User` when no user
 * is stored or the stored value is malformed — instead of throwing. This avoids
 * the `JSON.parse('')` -> `SyntaxError: Unexpected end of JSON input` crash that
 * occurred when components were constructed without a cached user (e.g. an
 * unauthenticated session). Mirrors the localStorage -> sessionStorage fallback
 * used for the auth token.
 */
export function getStoredUser(): User {
  const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user');
  if (!raw) {
    return {} as User;
  }
  try {
    return JSON.parse(raw) as User;
  } catch {
    return {} as User;
  }
}

export function setStoredUser(user: User): void {
  localStorage.setItem('user', JSON.stringify(user));
}

/**
 * The session token is written to both storages, as login always has. Readers
 * fall back from localStorage to sessionStorage.
 */
export function setStoredToken(token: string): void {
  localStorage.setItem('token', token);
  sessionStorage.setItem('token', token);
}

/** The stored session token (localStorage first, then sessionStorage), or '' when none. */
export function getStoredToken(): string {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
}

export function hasStoredSession(): boolean {
  return !!getStoredToken();
}

/** Removes the token and the user from both storages. */
export function clearStoredSession(): void {
  ['token', 'user'].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

/** The message login shows after the browser was signed out because the server refused its session. */
export const SESSION_ENDED_MESSAGE = 'You have been signed out. Please sign in again.';

const SESSION_ENDED_NOTICE_KEY = 'sessionEndedNotice';

/**
 * Leaves a one-shot note for the login page to show after a full page load,
 * which would lose a toast shown before it. It is not part of the stored
 * session, so {@link clearStoredSession} leaves it in place.
 */
export function setSessionEndedNotice(): void {
  sessionStorage.setItem(SESSION_ENDED_NOTICE_KEY, '1');
}

/** True once after {@link setSessionEndedNotice}: reading the note removes it. */
export function takeSessionEndedNotice(): boolean {
  const present = sessionStorage.getItem(SESSION_ENDED_NOTICE_KEY) !== null;
  sessionStorage.removeItem(SESSION_ENDED_NOTICE_KEY);
  return present;
}
